/* oxlint-ignore react/no-array-index-key */
import { Box, Flex, IconButton, Stack, Text, useClipboard } from '@chakra-ui/react'
import { faCheck, faCopy } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ClapButton from 'react-clap-button'

import type { ProgressionLevel } from '@/queries/constants'
import { getUserStoryUrl, getUserUrl } from '@/client/services/url'

import RequireLoginAndApproval from '@/components/Auth/RequireLoginAndApproval'
import ShareIconButton from '@/components/Share/ShareIconButton'
import ShwLink from '@/components/ShwLink'
import TextWithLines from '@/components/TextWithLines/TextWithLines'
import ModalV3 from '@/components/UI/Modal/ModalV3'
import Tippy from '@/components/UI/Tippy'

import UserProfile from './UserProfile'

const MAX_CLAPS_PER_SESSION = 10

type OnClapCountChange = ({
  count,
  countTotal,
  userStoryId,
}: {
  count: number
  countTotal: number
  userStoryId: number
}) => void

export interface ViewStoryAttributes {
  id: number
  country: string
  userId: number
  username: string | null
  name: string
  title: string
  body: string
  role: string
  clapCount?: number
  date: string
  avatarUrl: string
  progressionLevel: ProgressionLevel
}

export default function ViewStoryModal({
  story,
  storyClapCount = null,
  showStoryModal,
  onClose,
  onClapCountChange,
}: {
  story: ViewStoryAttributes | null
  storyClapCount?: number | null
  showStoryModal: boolean
  onClose: () => void
  onClapCountChange: OnClapCountChange
}): React.ReactElement | null {
  const link = getUserStoryUrl(story?.id ?? 0)
  const { hasCopied, onCopy } = useClipboard(link)
  return story === null ? null : (
    <ModalV3
      size="xl"
      open={showStoryModal}
      onClose={onClose}
      title={story.title}
      body={() => (
        <Box textAlign="center">
          <Flex mb="16px" justify="space-between" align="center">
            <ShwLink href={getUserUrl({ userId: story.userId, username: story.username })}>
              <UserProfile avatarUrl={story.avatarUrl} name={story.name} role={story.role} />
            </ShwLink>
            <Text mb="12px" textAlign="left" color="fg.subtle" fontWeight="bold">
              {story.date}
            </Text>
          </Flex>

          <Text mb="32px" textAlign="left">
            <TextWithLines text={story.body} maxLines={Number.POSITIVE_INFINITY} />
          </Text>
          <RequireLoginAndApproval>
            <ClapButton
              countTotal={storyClapCount ?? 0}
              maxCount={MAX_CLAPS_PER_SESSION}
              onCountChange={onClapCountChange}
            />
          </RequireLoginAndApproval>
          <Stack direction="row" spacing={1} alignSelf="end">
            {hasCopied ? (
              <IconButton
                variant="icon"
                aria-label="Copied"
                icon={<FontAwesomeIcon icon={faCheck} />}
              />
            ) : (
              <Tippy content="Copy story link">
                <IconButton
                  onClick={onCopy}
                  variant="icon"
                  aria-label="Copy"
                  icon={<FontAwesomeIcon icon={faCopy} />}
                />
              </Tippy>
            )}
            <ShareIconButton
              url={link}
              description={`Check out this ${story.role}'s story on Schoolhouse.world!`}
              shortDescription={`Check out this ${story.role}'s story on Schoolhouse.world!`}
              tooltipText="Share this story"
              square
            />
          </Stack>
        </Box>
      )}
    />
  )
}
